import { useEffect, useMemo, useState } from "react";
import { MainContentPaddingTypes, NewModal } from "../BaseComponents";
import { useAppSelector } from "../../Store";
import { Graph_QL, useLanControllers } from "../../Services";
import { colors, fonts, glyphs } from "../../Styles";
import { useAttentionLog } from "../../Services/Agrisys_GqlDataCaller/AttentionLogCalls/useAttentionLog";
import { useTranslation } from "react-i18next";
import { AttentionTypes } from "../../Definitions";
import { LocaleFarmDate } from "../../Definitions/localeFarmDate";
import { useLcIoOptions } from "../../Containers/FullViewScreens/GlobalScreens/Settings/_Settings/useLcIoOptions";
import { LanControllerInfo } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLanControllersCall";
import NewButton from "../BaseComponents/newButton";

interface SingleAttentionModal_Props{
  clickedLog: number;
  modalState: boolean;
  onClose: () => void;
  systemId: number | string
}

export default function SingleAttentionModal({ modalState, onClose, systemId, clickedLog}: SingleAttentionModal_Props) {
  const { t } = useTranslation();
  const [activeLogId, setActiveLogId] = useState<number>(clickedLog);

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm })), [farm]);
  const [technicalDataToggled, setTechnicalDataToggled] = useState(false);
  
  const { data: attentionLogState, maintainEntries: dismissAttentionLog} = useAttentionLog(connection);

  const logData = useMemo(() => {
    if (domain in attentionLogState && attentionLogState[domain] && typeof attentionLogState[domain] === "object" && "data" in attentionLogState[domain] && attentionLogState[domain].data && systemId) {
      return attentionLogState[domain].data.filter((log) => log.fk_systems === +systemId && log.dismissed !== true);
    }
    return []
  }, [attentionLogState, domain, systemId])

  const currentIndex = useMemo(() => {
    return logData.findIndex(logData => logData.attention_log_id === activeLogId);
  }, [logData, activeLogId]);

  const levelToString: Record<number, string> = {
    0: t(`systemControls.states.${AttentionTypes.alert}`, { defaultValue: "alert" }),
    1: t(`systemControls.states.${AttentionTypes.warning}`, { defaultValue: "warning" })
  };

  const typeToString: Record<number, string> = {
    1: t("systemControls.general.system", {defaultValue: "System"}),
    2: t("systemControls.general.motor", {defaultValue: "Motor"}),
    3: t("systemControls.general.animal", {defaultValue: "Animal"})
  }

  const activeLog = useMemo(() => {
    const log = logData.find(log => log.attention_log_id === activeLogId);
    if (!log) return null;

    const name = log.additionalInformation?.controller_name; 
    const cpu = log.additionalInformation?.lan_controller_cpu_id;
    const pen = (log.location_name !== undefined && log.location_name !== null) || (log.fk_locations !==  undefined && log.fk_locations !== null) ?  {Pen:log?.location_name ?? log?.fk_locations } : undefined
    const displayData = {
      ...pen,
      Time: new LocaleFarmDate(log.timestamp * 1000).toLocaleFarmString(),
      Type: typeToString[log.type],
      Level: levelToString[log.level],
      Message: log.message,
    };
    const locationId = log?.fk_locations !== null && log?.fk_locations !== undefined ? {Location_id: log.fk_locations} : undefined; 
    const ioDevice = log?.additionalInformation?.device_cpu_id !== undefined && log.additionalInformation.device_cpu_id !== null ? {"Io-device": `${log.additionalInformation.device_cpu_id}` + (log.additionalInformation.device_name !== undefined ? ` (${log.additionalInformation.device_name})` : "")} : undefined;
    const port = log?.additionalInformation?.port !== undefined && log.additionalInformation.port !== null ? {Port: `${log.additionalInformation.port}` + (log.additionalInformation.terminal_type !== undefined ? ` (${log.additionalInformation.terminal_type})` : "")} : undefined;
    
    const technicalData = {
      Attention_log_Id: log.attention_log_id,
      Lan_controller_log_id: log.lan_controller_log_id,
      ...locationId,
      Lan_controller: cpu !== undefined ? `${cpu}` + (name ? ` (${name})` : "") : null,
      ...ioDevice,
      ...port
    }
    
    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%", maxWidth: "320px", gap: "10px" }}>
        {Object.entries(displayData).map(([label, value]) => (
          <div key={label} style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "30px" }}>
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2 }}> {`${label.replace(/_/g, " ")}:`} </p>
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>
              {String(value)}
            </p>
          </div>
        ))}
        <div style={{ boxSizing: "border-box", borderTop: "2px solid "+colors.blueDarkSecondary }} />
        <div style={{ display: "flex", cursor: "pointer", alignItems: "center", WebkitTapHighlightColor: "transparent", gap: "5px"}} onClick={() => setTechnicalDataToggled(!technicalDataToggled)} >
          <p style={{...fonts.header,...fonts.fontSize4, color: colors.greyLightTertiary,marginBottom: "0px"}}> {t(`systemControls.general.technicalData`, { defaultValue: "Technical data" })} </p>
          <glyphs.Arrow transparency={technicalDataToggled ? 1 : 0.3} rotate={technicalDataToggled ? 90 : 0} transition={0.3} size="15px" />
        </div>
        {technicalDataToggled ? (
        Object.entries(technicalData).map(([label, value]) => (
        <div key={label} style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "30px" }}>
          <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2 }}> {`${label.replace(/_/g, " ")}:`} </p>
          <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>
            {String(value)}
          </p>
        </div>
        ))
          ) :  ( <></> )}
      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logData, activeLogId, technicalDataToggled]);

  return (
    <NewModal
      modalState={modalState}
      header={t("systemControls.general.attention", {defaultValue: "attention"})}
      // subHeader={`${t("systemControls.general.logid", {defaultValue: "log id"})} ${activeLogId}`}
      // subHeader={`# ${activeLogId}`}
      overrides={{
        mainContentPadding: MainContentPaddingTypes.secondary,
        mainContentScroll: true,
        secondaryContentScroll: true
      }}
      predefinedButtons={{
        onClose: onClose,
        onNext: () => setActiveLogId(logData[(currentIndex + 1) % logData.length].attention_log_id),
        onPrev: () => setActiveLogId(logData[(currentIndex - 1 + logData.length) % logData.length].attention_log_id)
      }}
      secondaryContent={activeLog ? <>{activeLog}</> : undefined}
      footerContent={
        <NewButton
          content={t("systemControls.general.delete", {defaultValue: "delete"})}
          onClick={() => {
            if (dismissAttentionLog) { dismissAttentionLog({ data: [activeLogId] }) }; setActiveLogId(logData[(currentIndex + 1) % logData.length].attention_log_id)
          }}
        />
      }
    />
  ) 
}